<template>
  <b-card no-body>
    <b-card-header>
      <b-row>
        <b-col cols="6">
          <h3 class="mb-0">PDF Downloads Report
          </h3>
        </b-col>
      </b-row>
    </b-card-header>
    <!-- Report Charts -->
    <report-charts ref="report_charts"></report-charts>
    <b-col cols="12" class="d-flex justify-content-center justify-content-sm-between align-items-center mb-3">
      <el-select
        class="select-primary pagination-select p-2"
        v-model="pagination.perPage"
        placeholder="Per page"
      >
        <el-option
          class="select-primary"
          v-for="item in pagination.perPageOptions"
          :key="item"
          :label="item"
          :value="item"
        >
        </el-option>
      </el-select>

      <div class="p-2">
        <el-date-picker
          v-model="dateValue"
          type="daterange"
          align="right"
          unlink-panels
          start-placeholder="Start date"
          end-placeholder="End date"
          value-format="yyyy-MM-dd"
          @input="filterByDate"
          :picker-options="pickerOptions">
        </el-date-picker>
      </div>

      <el-select v-model="SSAFilter" placeholder="Filter By SSA">
        <el-option
          v-for="item in ssaNameOptions"
          :key="item.name"
          :label="item.label"
          :value="item.name">
        </el-option>
      </el-select>

      <el-select v-model="SSEFilter" placeholder="Filter By SSE">
        <el-option
          v-for="item in sseNameOptions"
          :key="item.name"
          :label="item.label"
          :value="item.name">
        </el-option>
      </el-select>

      <div class="p-2">
        <base-input
          v-model="searchQuery"
          v-on:input="debouncedHandler"
          prepend-icon="fas fa-search"
          placeholder="Search...(case-sensitive)"
        >
        </base-input>
      </div>
    </b-col>
    <el-table
      class="table-responsive align-items-center table-flush"
      header-row-class-name="thead-light"
      :data="queriedData"
      @sort-change="sortChange"
      @selection-change="selectionChange"
      row-key="id"
    >

      <el-table-column
        label="Customer Name"
        prop="customer_name"
        min-width="180px"
        sortable
      >
      </el-table-column>

      <el-table-column
        label="Net Income"
        prop="net_income"
        min-width="150px"
        sortable
      >
        <template v-slot="{row}">
          <div v-if="row.net_income != undefined">
            {{parseFloat(row.net_income).toLocaleString(undefined, {minimumFractionDigits: 2})}}            
          </div>
        </template>
      </el-table-column>

      <el-table-column
        label="Commitment"
        prop="commitment"
        min-width="150px"
        sortable
      >
        <template v-slot="{row}">
          <div v-if="row.commitment != undefined">
            {{parseFloat(row.commitment).toLocaleString(undefined, {minimumFractionDigits: 2})}}            
          </div>
        </template>
      </el-table-column>

      <el-table-column
        label="Variable Expenses"
        prop="variable_expenses"
        min-width="150px"
        sortable
      >
        <template v-slot="{row}">
          <div v-if="row.variable_expenses != undefined">
            {{parseFloat(row.variable_expenses).toLocaleString(undefined, {minimumFractionDigits: 2})}}            
          </div>
        </template>
      </el-table-column>
      
      <el-table-column
        label="Cash Flow"
        prop="cash_flow"
        min-width="150px"
        sortable
      >
        <template v-slot="{row}">
          <div v-if="row.cash_flow != undefined">
            <template v-if="row.cash_flow < 0">
              <span class="text-red">({{parseFloat(Math.abs(row.cash_flow)).toLocaleString(undefined, {minimumFractionDigits: 2})}})</span>
            </template>
            <template v-else>
              <span class="text-green">{{parseFloat(row.cash_flow).toLocaleString(undefined, {minimumFractionDigits: 2})}}</span>            
            </template>
          </div>
        </template>  
      </el-table-column>
      <el-table-column
        label="Net Worth"
        prop="net_worth"
        min-width="150px"
        sortable
      >
        <template v-slot="{row}">
          <div v-if="row.net_worth != undefined">
            <template v-if="row.net_worth < 0">
              <span class="text-red">({{parseFloat(Math.abs(row.net_worth)).toLocaleString(undefined, {minimumFractionDigits: 2})}})</span>
            </template>
            <template v-else>
              <span class="text-green">{{parseFloat(row.net_worth).toLocaleString(undefined, {minimumFractionDigits: 2})}}</span>            
            </template>
          </div>
        </template>  
      </el-table-column>

      <el-table-column label="History" prop="download_report_log_count" min-width="130px">
        <template v-slot="{row}">
          <base-button type="primary" icon size="sm" @click.prevent="onShowLog(row)">
            <span class="btn-inner--text">Log ({{ row.download_report_log_count }})</span>
          </base-button>
        </template>
      </el-table-column>

      <el-table-column
        label="PDF Download"
        prop="pdf_download"
        min-width="150px"
        sortable
      >
        <template v-slot="{row}">
          <div class="table-actions btn-group-vertical report-action">
            <base-button v-if="row.financial_proposal_file.attach_file" type="primary" icon size="sm" @click.prevent="onViewPDF(row)">
              <span class="btn-inner--text">View</span>
            </base-button>
            <base-button v-if="row.financial_proposal_file.attach_file" type="primary" icon size="sm" @click.prevent="onDownload(row)">
              <span class="btn-inner--text">Download</span>
            </base-button>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        label="IM2U Customer ID"
        prop="im2u_customer_id"
        min-width="150px"
        sortable
      ></el-table-column>

      <el-table-column
        label="SSE / SSA"
        prop="agent_name"
        min-width="150px"
        sortable
      ></el-table-column>

      <el-table-column
        label="PDF Generated Date"
        prop="pdf_generated_date"
        min-width="150px"
        sortable
      ></el-table-column>
    </el-table>

    <!-- <base-button type="primary" icon size="sm" @click.prevent="test()">
      <span class="btn-inner--text">test</span>
    </base-button> -->

    <div
      slot="footer"
      class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
    >
      <div>
        <p class="card-category">
          Showing {{ from + 1 }} to {{ to }} of {{ total }} entries

          <span v-if="selectedRows.length">
            &nbsp; &nbsp; {{selectedRows.length}} rows selected
          </span>
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        :current="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
        @change="paginationChanged($event)"
      >
      </base-pagination>
    </div>
  </b-card>
</template>

<script>
  import ReportCharts from '@/views/FinancialProposal/PdfDownloadReportsFolder/ReportCharts';
  import { Table, TableColumn, DropdownMenu, DropdownItem, Dropdown, Tooltip, Select, Option, DatePicker } from 'element-ui'
  import axios from 'axios'
    import {apiDomain, getHeader} from '@/assets/js/config.js'
  
  import 'sweetalert2/dist/sweetalert2.css'
  import { BasePagination } from '@/components'
  import clientPaginationMixin from '@/assets/js/clientPaginationMixin'
  import debounce from 'lodash.debounce'

  export default {
    name: 'pdf-download-reports-table',
    mixins: [clientPaginationMixin],
    components: {
      ReportCharts,
      BasePagination,
      [Select.name]: Select,
      [Option.name]: Option,
      [Tooltip.name]: Tooltip,
      [Table.name]: Table,
      [TableColumn.name]: TableColumn,
      [Dropdown.name]: Dropdown,
      [DropdownItem.name]: DropdownItem,
      [DropdownMenu.name]: DropdownMenu,
      [DatePicker.name]: DatePicker,
    },
    data() {
      return {
        propsToSearch:['customer_name'],
        tableData: [],
        currentPage: 1,
        mode: 'listing',
        selectedRows: [],
        searchText: '',
        debouncedHandler: '',
        TableColumn:[{
          type: 'selection'
        }],
        pickerOptions: {
          shortcuts: [{
            text: 'Last week',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
              picker.$emit('pick', [start, end]);
            }
          }, {
            text: 'Last month',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
              picker.$emit('pick', [start, end]);
            }
          }, {
            text: 'Last 3 months',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
              picker.$emit('pick', [start, end]);
            }
          }],
          // onPick: this.filterByDate
        },
        value1: '',
        dateValue: [],
        ssaNameOptions: [{
          name: 'Ruby',
          label: 'Ruby'
        }, {
          name: 'Jennie',
          label: 'Jennie'
        }, {
          name: 'Rose',
          label: 'Rose'
        }],
        SSAFilter: '',
        sseNameOptions: [{
          name: 'Ruby',
          label: 'Ruby'
        }, {
          name: 'Jennie',
          label: 'Jennie'
        }, {
          name: 'Rose',
          label: 'Rose'
        }],
        SSEFilter: '',
      };
    },
    created(){
      this.pdfDownloadReportList();
    },
    mounted(){
      this.debouncedHandler = debounce(() => {
        // this.searchFinancialProposalList();
      }, 500);
    },
    methods: {
      paginationChanged(page) {
        this.pagination.currentPage = page
      },
      selectionChange(selectedRows) {
        this.selectedRows = selectedRows
      },
      pdfDownloadReportList() {
        axios.get(apiDomain + 'financial_proposal/pdf_downloads_report', { headers: getHeader() })
        .then(response => {
          this.tableData = response.data.data
        })
        .catch ( error => {
           
          _gNotif.errorFunction(error,null, "Show PDF Downloads Report") 
        });
      },
      onShowLog(row) {
          this.$router.push({path: '/pdf_download_report_log/' + row.im2u_customer_id})
      },
      onViewPDF(row) {
        axios.get(apiDomain + 'financial_proposal/download_financial_proposal_report/' + row.financial_proposal_file.financial_proposal_file_id + '/view' ,{ headers: getHeader(), responseType: "blob" })
        .then ( response => {
          if (response.status === 200)
          {
              var FILE = window.URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}));        
              var docUrl = document.createElement('a');
              docUrl.href = FILE;
              docUrl.setAttribute('download', row.attach_file);
              document.body.appendChild(docUrl);
              window.open(docUrl);
          }
        })
        .catch ( error => {
            _gNotif.errorFunction(error,null, "Download Financial Proposal Report")
        });
      },
      onDownload(row) {
        axios.get(apiDomain + 'financial_proposal/download_financial_proposal_report/' + row.financial_proposal_file.financial_proposal_file_id + '/download' ,{ headers: getHeader(), responseType: "blob" })
        .then ( response => {
          if (response.status === 200)
          {
            var FILE = window.URL.createObjectURL(new Blob([response.data]));        
            var docUrl = document.createElement('a');

            docUrl.href = FILE;
            docUrl.setAttribute('download', row.financial_proposal_file.attach_file);
            document.body.appendChild(docUrl);
            docUrl.click();

            if(this.$refs.report_charts != undefined)
            {
              this.$refs.report_charts.generateChart();
            }
          }
        })
        .catch ( error => {
            _gNotif.errorFunction(error,null, "Download Financial Proposal Report")
        });
      },
      filterByDate() {
        if (this.dateValue) {
          let startDate = this.dateValue[0];
          let endDate = this.dateValue[1];

          axios.get(apiDomain + 'financial_proposal/filter_financial_proposal_report_by_date/' + startDate + '/' + endDate, { headers: getHeader() })
          .then(response => {
            this.tableData = response.data.data
          })
          .catch ( error => {
              _gNotif.errorFunction(error,null, "Filter By Date")
          });
        }
      }
    }
  }
</script>

<style>
  .el-table .el-table__header-wrapper thead th .cell {
    word-break: inherit;
  }
  .form-group {
    margin-bottom: 0;
  }
</style>
<style scoped>
.report-action {
  width: 80px
}
</style>